import { useState } from "react";
import { Mail, Shield, Lock, CreditCard, Copy, Check, Eye } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const HelpFAQ = () => {
  const [emailRevealed, setEmailRevealed] = useState(false);
  const [copied, setCopied] = useState(false);

  const faqs = [
    {
      id: "credits",
      icon: Shield,
      title: "How Credits Work",
      content: `Credits power AI actions in GreyRock AI. They never expire and are added instantly upon purchase.

• **New accounts** start with 3 one-time credits

**Credit Bundles (One-Time Purchase):**
• **Starter Pack ($9.99)**: 25 credits · ~$0.40/credit
• **Pro Pack ($24.99)**: 80 credits · ~$0.31/credit — Best Value
• **10-Credit Pack ($4.99)**: 10 credits · ~$0.50/credit

**Credit Usage:**
• 1 credit — Full forensic analysis
• 1 credit — Response strategy
• 2 credits — Rehearse Mode (full simulation + debrief)
• 2 credits — Content / Script Factory generation
• Free — Exporting already-generated outputs
• Free — Feed, Insights, and Reports browsing

**Credit Policy:**
• Credits never expire
• Credits are additive — every purchase adds to your balance
• 7-day refund policy on all purchases`,
    },
    {
      id: "privacy",
      icon: Lock,
      title: "Privacy & Security",
      content: `Your privacy is our top priority. Here's how we protect your data:

• **End-to-End Encryption**: All documents are encrypted at rest and in transit
• **No Data Selling**: We never sell, share, or monetize your personal data
• **Secure Storage**: Documents are stored in isolated, private cloud buckets
• **User-Only Access**: Row-Level Security ensures only you can access your files
• **AI Processing**: Text is processed in real-time and not stored after analysis
• **Account Deletion**: Request full data deletion anytime via support

We're built on Lovable Cloud's enterprise-grade infrastructure.`,
    },
    {
      id: "billing",
      icon: CreditCard,
      title: "Billing",
      content: `All payments are one-time purchases processed securely through Stripe.

**Starter Pack — $9.99**
• 25 credits added instantly
• Full forensic analysis, response strategy, content generation

**Pro Pack — $24.99 — Best Value**
• 80 credits added instantly
• Best value per credit (~$0.31/credit)

**10-Credit Pack — $4.99**
• 10 credits added instantly
• Buy on its own or add to your balance anytime

**Refund Policy:**
If you're not satisfied, contact us within 7 days of purchase for a full refund. No questions asked.

**Payment Methods:**
We accept all major credit cards, debit cards, and Apple Pay / Google Pay.

Credits never expire and are added to your account instantly upon purchase.`,
    },
  ];

  // Construct email only when needed (spam-proof)
  const getSupportEmail = () => {
    const user = "support";
    const domain = "greyrockai.app";
    return `${user}@${domain}`;
  };

  const handleContactSupport = () => {
    const email = getSupportEmail();
    const subject = encodeURIComponent("[GreyRock Support]");
    window.location.href = `mailto:${email}?subject=${subject}`;
  };

  const handleRevealEmail = () => {
    setEmailRevealed(true);
  };

  const handleCopyEmail = async () => {
    const email = getSupportEmail();
    try {
      await navigator.clipboard.writeText(email);
      setCopied(true);
      toast.success("Email copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Failed to copy email");
    }
  };

  return (
    <div className="space-y-6">
      <Accordion type="single" collapsible className="space-y-3">
        {faqs.map((faq) => (
          <AccordionItem
            key={faq.id}
            value={faq.id}
            className={cn(
              "rounded-xl border border-glass-border bg-charcoal-medium",
              "data-[state=open]:border-cyan/30"
            )}
          >
            <AccordionTrigger
              className={cn(
                "px-4 py-4 hover:no-underline",
                "[&[data-state=open]>svg]:rotate-180"
              )}
            >
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-cyan/10">
                  <faq.icon className="w-4 h-4 text-cyan" />
                </div>
                <span className="font-medium text-left">{faq.title}</span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-4 pb-4">
              <div className="pl-11 text-sm text-muted-foreground whitespace-pre-line leading-relaxed">
                {faq.content}
              </div>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>

      {/* Divider */}
      <div className="border-t border-glass-border pt-6">
        <p className="text-xs text-muted-foreground text-center mb-4">
          Can't find what you're looking for?
        </p>
        
        {/* Spam-proof contact support - buried at the very bottom */}
        <div className="space-y-3">
          {/* Click to Reveal email pattern - shown first as less prominent */}
          <div className="flex items-center gap-2">
            {emailRevealed ? (
              <div className="flex-1 flex items-center gap-2 px-4 py-3 rounded-xl bg-charcoal-medium border border-glass-border">
                <span className="flex-1 font-mono text-sm text-foreground">
                  {getSupportEmail()}
                </span>
                <button
                  onClick={handleCopyEmail}
                  className="p-1.5 rounded-lg hover:bg-charcoal-light transition-colors"
                  title="Copy email"
                >
                  {copied ? (
                    <Check className="w-4 h-4 text-cyan" />
                  ) : (
                    <Copy className="w-4 h-4 text-muted-foreground" />
                  )}
                </button>
              </div>
            ) : (
              <button
                onClick={handleRevealEmail}
                className={cn(
                  "flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl",
                  "bg-charcoal-medium border border-glass-border",
                  "text-muted-foreground hover:text-foreground hover:border-cyan/30 transition-colors"
                )}
              >
                <Eye className="w-4 h-4" />
                <span className="text-sm">Click to Reveal Support Email</span>
              </button>
            )}
          </div>

          {/* Contact Support button */}
          <button
            onClick={handleContactSupport}
            className={cn(
              "w-full flex items-center justify-center gap-2 px-5 py-3 rounded-xl font-medium",
              "bg-charcoal-medium hover:bg-charcoal-light border border-glass-border",
              "text-muted-foreground hover:text-foreground transition-colors"
            )}
          >
            <Mail className="w-5 h-5" />
            Contact Support
          </button>
        </div>
      </div>
    </div>
  );
};

export default HelpFAQ;
